import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Calculator, Fuel, TrendingUp } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToTop } from "@/components/BackToTop";
import { seo } from "@/lib/seo";
import { CalculadorasHubSkeleton } from "@/components/skeletons";

const PAGE_TITLE = "Calculadoras Financeiras — Seu Bolso Tranquilo";
const PAGE_DESCRIPTION =
  "Ferramentas gratuitas para planejar seu dinheiro e gastos: simulador de juros compostos, gasolina x etanol e mais. Tudo em português, sem cadastro.";

const CALCULATORS = [
  {
    title: "Calculadora de Juros Compostos",
    description:
      "Projete quanto seu dinheiro rende ao longo do tempo com capital inicial, aportes mensais e taxa de juros.",
    href: "/calculadoras/juros-compostos" as const,
    icon: TrendingUp,
  },
  {
    title: "Gasolina x Etanol",
    description:
      "Descubra qual combustível vale mais a pena informando o preço do litro do álcool e da gasolina.",
    href: "/calculadoras/gasolina-etanol" as const,
    icon: Fuel,
  },
];

export const Route = createFileRoute("/calculadoras/")({
  head: () =>
    seo({
      path: "/calculadoras",
      title: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      breadcrumbs: [
        { name: "Início", path: "/" },
        { name: "Calculadoras", path: "/calculadoras" },
      ],
    }),
  component: CalculatorsIndex,
  pendingComponent: CalculadorasHubSkeleton,
});

function CalculatorsIndex() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">Início</Link>
          <span className="mx-2">/</span>
          <span className="text-foreground">Calculadoras</span>
        </nav>

        <div className="max-w-3xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-4">
            <Calculator className="size-4" />
            Ferramentas gratuitas
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Calculadoras Financeiras
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Simuladores rápidos para te ajudar a planejar dinheiro, investimentos e metas — sem
            cadastro e em português.
          </p>
        </div>

        <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {CALCULATORS.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.href}
                to={c.href}
                className="group rounded-3xl border border-border/60 bg-card p-6 hover:border-primary/50 hover:bg-card/80 transition-colors flex flex-col"
              >
                <div className="size-10 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-4">
                  <Icon className="size-5" />
                </div>
                <h2 className="text-lg font-semibold mb-2">{c.title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">
                  {c.description}
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
                  Abrir calculadora <ArrowRight className="size-4" />
                </span>
              </Link>
            );
          })}
        </section>

        <p className="text-sm text-muted-foreground mt-12">
          Mais calculadoras em breve. Tem alguma sugestão?{" "}
          <Link to="/contato" className="text-primary hover:underline">Fale com a gente</Link>.
        </p>
      </main>

      <BackToTop />
    </div>
  );
}